import {Button, Col, Form, ModalBody, ModalFooter, Row, Spinner, Tab, Table, Tabs} from "react-bootstrap";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCancel, faCircleArrowDown, faEdit, faPlus, faTrash} from "@fortawesome/free-solid-svg-icons";
import LoadDataFromServerError from "../../components/LoadDataFromServerError";
import {useEffect, useMemo, useState} from "react";
import {dateTimeToString, dateToString, dateWithZeroTime} from "../../tools";
import {
    RRDEPARTMENT_CETARING,
    RRDEPARTMENT_MONTAGE,
    RRDEPARTMENT_MONTAGE_LABEL,
    RRDepartmentType,
    RRDEPARTMENT_CETARING_LABEL
} from "../../types/RRDepartmentType";
import RRTable from "../../components/rr-table/RRTable";
import {collection, orderBy, query, QueryConstraint, where, writeBatch} from "firebase/firestore";
import {useCollection} from "react-firebase-hooks/firestore";
import {appFirestore} from "../../../index";
import {RREventsType, RREventType} from "../../types/RREventType";
import {
    RREventStatusAll,
    RREventStatusCompleted,
    RREventStatusCurrent, RREventStatusCurrentList,
    RREventStatusRejected
} from "../../types/RREventStatusType";
import {eventsDataConverter} from "../../../firebase/FirebaseEventsLoader";
import ReactDatePicker from "react-datepicker";
import {useAppSelector} from "../../hooks";
import {selectDepartment} from "../../../features/AppStateSlice";

export interface Props {
    isVisible: boolean;
    onAdd: () => any;
    onEdit: (item: RREventType) => any;
    onDelete: (item: RREventType) => any;
    onClose: (result: any) => any;
    onSelect?: (result: any) => any;
}

interface TableFilter {
    nomer?: string;
    name?: string;
    client?: string;
    start?: Date;
    end?: Date;
    status?: string;
}
function DepartmentAsString(props: { department: RRDepartmentType }) {
    if (props.department === RRDEPARTMENT_MONTAGE) {
        return <>{RRDEPARTMENT_MONTAGE_LABEL}</>;
    }
    if (props.department === RRDEPARTMENT_CETARING) {
        return <>{RRDEPARTMENT_CETARING_LABEL}</>;
    }
    return null;
}

export function EventsList({isVisible, onAdd, onEdit, onDelete, onClose, onSelect}: Props) {
    const department = useAppSelector(selectDepartment);
    const [filter, setFilter] = useState<TableFilter>({});
    const [listMode, setListMode] = useState<string>("CURRENT");
    const eventsConstraints = useMemo<QueryConstraint[]>(
        () => {
            const qConstr = [where("__name__", "!=", "WAREHOUSE")];
            qConstr.push(where("isDeleted", "==", false));
            if(department === "catering") qConstr.push(where("isCatering", "==", true));
            if(department === "montage") qConstr.push(where("isMontage", "==", true));
            if(department === "store") qConstr.push(where("isStore", "==", true));

            if (listMode === "CURRENT") {
                qConstr.push(where("eventStatus", "in", RREventStatusCurrentList));
            }

            if (listMode === "COMPLETED") {
                qConstr.push(where("eventStatus", "in", RREventStatusCompleted));
            }

            if (listMode === "REJECTED") {
                qConstr.push(where("eventStatus", "in", RREventStatusRejected));
            }

            return qConstr;
        }, [listMode]);
    const eventsQuery = useMemo(
        () => query(collection(appFirestore, "events"), ...eventsConstraints, orderBy('__name__')),
        [eventsConstraints]
    );
    const [eventsData, loading, error] = useCollection(eventsQuery);
    const [events, setEvents] = useState<RREventsType>([]);

    useEffect(() => {
        if (eventsData) {
            setEvents(eventsDataConverter(eventsData.docs).sort(
                (a, b) => a.firstDate! >= b.firstDate! ? 1 : -1
            ));
        } else {
            setEvents([]);
        }
    }, [eventsData]);

    const filteredEvents = useMemo(() => {
        let result = [...events];
        if(filter.nomer) {
            result = result.filter(r => r.eventNumber.toLowerCase().indexOf(filter.nomer!.toLowerCase()) > -1)
        }

        if(filter.name) {
            result = result.filter(r => r.eventName.toLowerCase().indexOf(filter.name!.toLowerCase()) > -1)
        }

        if(filter.client) {
            result = result.filter(r => r.clientName.toLowerCase().indexOf(filter.client!.toLowerCase()) > -1)
        }

        if(filter.status) {
            result = result.filter(r => r.eventStatus.toLowerCase().indexOf(filter.status!.toLowerCase()) > -1)
        }

        if(filter.start) {
            result = result.filter(r => r.firstDate && r.firstDate >= filter.start!)
        }

        if(filter.end) {
            result = result.filter(r => !r.lastDate || dateWithZeroTime(r.lastDate)! <= filter.end!)
        }

        return result;
    }, [events, filter]);

    function isLoading() {
        return loading;
    }

    if (!isVisible) {
        return null;
    }

    if(error) console.error(error);

    return (
        <>
            <ModalBody>
                <Row>
                    <Col>
                        <Tabs activeKey={listMode}
                              onSelect={eventKey => setListMode(eventKey || "CURRENT")}>
                            <Tab title={"Текущи"} eventKey={"CURRENT"}/>
                            <Tab title={"Приключени"} eventKey={"COMPLETED"}/>
                            <Tab title={"Отказани"} eventKey={"REJECTED"}/>
                        </Tabs>
                    </Col>
                </Row>

                {isLoading() && <Spinner animation={"border"}/>}

                {
                    !isLoading() && error && <LoadDataFromServerError/>
                }

                {
                    !isLoading() && isVisible && events.length === 0 &&
                    <Row className={"m-5"}>
                        <Col className={"text-center fw-bold"}>Няма дефинирани събития</Col>
                    </Row>
                }


                {
                    !isLoading() && !error && events && isVisible && events.length > 0 &&
                    <>
                        <div className={"min-h-400px max-h-65vh scrollable"}>
                            <RRTable>
                                <thead>
                                <tr>
                                    <th></th>
                                    <th>Номер</th>
                                    <th>Събитие</th>
                                    {/*<th>Отдел</th>*/}
                                    <th>Клиент</th>
                                    <th>Начало</th>
                                    <th>Край</th>
                                    <th>Статус</th>
                                </tr>
                                <tr>
                                    <th></th>
                                    <th>
                                        <Form.Control
                                            size={"sm"}
                                            value={filter.nomer}
                                            onChange={e => setFilter({...filter, nomer: e.target.value})}
                                        />
                                    </th>
                                    <th>
                                        <Form.Control
                                            size={"sm"}
                                            value={filter.name}
                                            onChange={e => setFilter({...filter, name: e.target.value})}
                                        />
                                    </th>
                                    <th>
                                        <Form.Control
                                            size={"sm"}
                                            value={filter.client}
                                            onChange={e => setFilter({...filter, client: e.target.value})}
                                        />
                                    </th>
                                    <th>
                                        <ReactDatePicker
                                            className={"form-control form-control-sm"}
                                            dateFormat="dd.MM.yyyy"
                                            selected={filter.start}
                                            onChange={date => setFilter({...filter, start: date || undefined})}
                                        />
                                    </th>
                                    <th>
                                        <ReactDatePicker
                                            className={"form-control form-control-sm"}
                                            dateFormat="dd.MM.yyyy"
                                            selected={filter.end}
                                            onChange={date => setFilter({...filter, end: date || undefined})}
                                        />
                                    </th>
                                    <th>
                                        <Form.Select
                                            size={"sm"}
                                            value={filter.status}
                                            onChange={option => setFilter({...filter, status: option.target.value})}
                                        >
                                            <option />
                                            {
                                                RREventStatusAll.map(
                                                    o => o === "WAREHOUSE" ? null : <option key={o} value={o}>{o}</option>
                                                )
                                            }
                                        </Form.Select>
                                    </th>
                                </tr>
                                </thead>
                                <tbody>
                                <tr>
                                    <td colSpan={8}></td>
                                </tr>
                                {
                                    filteredEvents.length > 0 && filteredEvents.map(
                                        d =>
                                            <tr key={d.id} className={"tr-bordered"}>
                                                <td className={"w-75px"}>
                                                    <Button size={"sm"} variant={"outline-secondary"}
                                                            onClick={() => onEdit(d)}
                                                    >
                                                        <FontAwesomeIcon icon={faEdit}/>
                                                    </Button>&nbsp;
                                                    <Button size={"sm"} variant={"outline-danger"}
                                                            onClick={() => {
                                                                onDelete(d)
                                                            }}
                                                    >
                                                        <FontAwesomeIcon icon={faTrash}/>
                                                    </Button>&nbsp;
                                                    {
                                                        onSelect &&
                                                        <Button size={"sm"} variant={"outline-success"}
                                                                onClick={() => {
                                                                    onSelect(d)
                                                                }}
                                                        >
                                                            <FontAwesomeIcon icon={faCircleArrowDown}/>
                                                        </Button>
                                                    }
                                                </td>
                                                <td className={"td-left-dbl-border"}> {d.eventNumber} </td>
                                                <td className={"td-left-dbl-border"}> {d.eventName} </td>
                                                {/*<td className={"td-left-dbl-border"}> {d.eventCategory} </td>*/}
                                                <td className={"td-left-dbl-border"}> {d.clientName}</td>
                                                <td className={"td-left-dbl-border"}> {dateTimeToString(d.firstDate)} </td>
                                                <td className={"td-left-dbl-border"}> {dateTimeToString(d.lastDate)} </td>
                                                <td className={"td-left-dbl-border"}> {d.eventStatus}</td>
                                            </tr>
                                    )
                                }
                                </tbody>

                            </RRTable>
                        </div>
                    </>
                }
            </ModalBody>

            <ModalFooter>
                <Button onClick={() => onAdd()}>
                    <FontAwesomeIcon icon={faPlus}/> Добави ново
                </Button>
                <Button variant={"outline-secondary"} onClick={() => {
                    onClose(null)
                }}>
                    <FontAwesomeIcon icon={faCancel}/> Затвори
                </Button>
            </ModalFooter>
        </>
    )
}
